<template lang="pug">
  .upload-files
    slot(name="title")
      .field.is-horizontal: label#upload-component-title.upload-title.label.name.is-pulled-left {{ title }}
    .drop-active(v-show="$refs.upload && $refs.upload.dropActive")
      slot(name="dropLabel")
        h3 {{ gettext('Drop files to upload') }}
    .upload.field
      .table-responsive(v-if="!files.length && drop")
        .field.is-horizontal.has-text-centered
          h4.drop-title {{ gettext('Drop files anywhere to upload') }}
        .field.is-horizontal.has-text-centered
          label.button.is-light(:for="name") {{ gettext('Select Files') }}
      .files-field(v-else)
        .columns.is-mobile
          .column.is-4(v-for="(file, index) in files" :key="file.id")
            .notification.file(:style="{ 'background-image': 'url(' + file.preview + ')'}")
              button.delete(@click="removeItem(file)" :title="gettext('Remove')")
              .loading-item(v-if="(file.active || file.progress !== '0.00') && hasProgress && !file.success && !file.error")
                v-progress(:progress="parseInt(file.progress)")
                  strong {{ file.progress || file.thumb}}%
                  small ({{file.size | formatSize}})
              .loading-error(v-else-if="file.error" :title="gettext('The file will not be uploaded!')")
                .error-block {{serverErrorHandler(file.error)}}
              .loading-success(v-else-if="!file.active && file.success")
                .mdi.mdi-check.success(:title="gettext('Done')")
              .loading-active(v-else)
                strong {{ gettext('wait') }}...

      .actions-btns
        vue-upload-component.btn.btn-primary.dropdown-toggle(
          ref="upload"
          v-model="files"
          :extensions="extensions"
          :accept="accept"
          :custom-action="fileAction"
          :multiple="multiple"
          :directory="directory"
          :size="size || 0"
          :thread="thread < 1 ? 1 : (thread > 5 ? 5 : thread)"
          :drop="drop"
          :drop-directory="dropDirectory"
          :add-index="addIndex"
          :maximum="maximum"
          @input-file="inputFile"
          @input-filter="inputFilter"
        )
        label.button.is-light.add-new(:for="name" v-if="files.length") Add new files
        button.btn.btn-success(type="button" v-if="(!$refs.upload || !$refs.upload.active) && !autoUpload" @click.prevent="$refs.upload.active = true")
          i.fa.fa-arrow-up(aria-hidden="true") {{ gettext('Start Upload') }}
</template>
<script>
import VueUploadComponent from 'vue-upload-component'
import { gettext } from '@/services/TranslateHandler'
import VProgress from './parts/circle-progress'

export default {
  name: 'UploadFiles',
  data () {
    return {
      files: []
    }
  },
  components: {
    VueUploadComponent,
    VProgress
  },
  props: {
    title: {
      default: gettext('Upload files')
    },
    accept: {
      default: 'image/png,image/gif,image/jpeg,application/pdf'
    },
    extensions: {
      default: () => ['jpg', 'png', 'pdf']
    },
    name: {
      default: 'file'
    },
    thread: {
      default: 3
    },
    minSize: {
      default: 1024
    },
    size: {
      default: () => (1024 * 1024 * 8)
    },
    multiple: {
      default: true
    },
    maximum: {
      default: 0
    },
    directory: {
      default: false
    },
    drop: {
      default: true
    },
    dropDirectory: {
      default: false
    },
    addIndex: {
      default: false
    },
    autoUpload: {
      default: true
    },
    thumbs: {
      default: true
    },
    uploadAction: {
      default: () => {
        return (data) => {
          return new Promise(resolve => {
            resolve(data)
          })
        }
      }
    },
    hasProgress: {
      default: true
    },
    preview: {
      default: ''
    },
    setData: {
      default: () => []
    }
  },
  filters: {
    formatSize (size) {
      return parseFloat((size / 1024 / 1024).toFixed(2)) + 'MB'
    }
  },
  methods: {
    gettext: gettext,
    async inputFile (data) {
      if (this.autoUpload && data && !this.$refs.upload.active) {
        this.$refs.upload.active = true
      }
    },
    inputFilter (newFile, oldFile, prevent) {
      if (newFile && !oldFile) {
        // Filter system files
        if (/(\/|^)(Thumbs\.db|desktop\.ini|\..+)$/.test(newFile.name)) {
          return prevent()
        }
        if (/\.(php5?|html?|jsx?)$/i.test(newFile.name)) {
          return prevent()
        }
        // For thumbs
        if (newFile && (!oldFile || newFile.file !== oldFile.file) && this.thumbs) {
          newFile.blob = ''
          let URL = window.URL || window.webkitURL
          if (URL && URL.createObjectURL) {
            newFile.blob = URL.createObjectURL(newFile.file)
          }
          newFile.thumb = ''
          if (newFile.blob && newFile.type.substr(0, 6) === 'image/') {
            newFile.thumb = newFile.blob
          }
        }
      }
    },
    async fileAction (data) {
      const progress = {
        onUploadProgress: (progressEvent) => {
          data.progress = Math.round((progressEvent.loaded * 100) / progressEvent.total)
        }
      }
      data.base64 = await this.toBase64(data.file)
      await this.uploadAction(data, progress)
      return data
    },
    toBase64 (data) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.readAsDataURL(data)
        reader.onload = () => resolve(reader.result)
        reader.onerror = error => reject(error)
      })
    },
    serverErrorHandler (err) {
      if (err instanceof Object) {
        if (((err.data || {}).error || {}).message) {
          return err.data.error.message
        }
        return gettext('error')
      }
      switch (err) {
        case 'size': return gettext('file size is too large')
        case 'extension': return gettext('file extension is not supported')
      }
      return err
    },
    removeItem (item) {
      this.$refs.upload.remove(item)
    }
  },
  watch: {
    files: {
      deep: true,
      handler: function (val) {
        if (val) {
          this.$emit('input', val.filter(item => item.success))
        }
      }
    },
    setData: {
      deep: true,
      handler: function (val) {
        if (val.length) {
          this.files = val
        }
      }
    }
  },
  mounted () {
    this.$watch(
      () => {
        return this.$refs.upload.active
      },
      (val) => {
        this.$emit('active', val)
      }
    )
  }
}
</script>
<style lang="scss">
.upload-files {
  width: 100%;
  .upload {
    background-color: #fff;
    padding: 5px;
    display: block;
    .drop-title {
      margin-top: 16px;
    }
    label.button{
      &.add-new {
        width: 100%;
        margin-top: 5px;
      }
      background-color: #e7e7e7;
      border-radius: 0;
      &:hover {
        background-color: #eee;
      }
    }
    table {
      th, td {
        vertical-align: middle;
      }
    }
    .field {
      display: block;
    }
    &:hover {
      background-color: #fbfbfb;
    }
    .files-field {
      .columns {
        flex-wrap: wrap;
      }
      .file {
        padding: 1.25rem;
        border-radius: 0;
        height: 150px;
        background-size: contain;
        background-repeat: no-repeat;
        button.delete {
          position: absolute;
          &::before {
            width: 10px;
          }
        }
      }
      .loading-error, .loading-success, .loading-active {
        align-content: center;
        display: grid;
        margin: auto;
      }
      .loading-success {
        color: #00d100;
        font-size: 48px;
        text-shadow: 1px 1px 1px #fbfbfb, 1px 1px 1px #262626;
        .success {
          cursor: unset;
        }
      }
    }
  }

  .btn-group {
    .dropdown-menu {
      display: block;
      visibility: hidden;
      transition: all .2s;
    }
    &:hover {
      .dropdown-menu {
        visibility: visible;
      }
    }
    .dropdown-toggle {
      margin-right: .6rem;
    }
    .delete-btn {
      font-size: 24px;
      text-align: center;
      color: #b4b4b4;
      &:hover {
        color: #bb1d2c;
      }
    }
  }
  label.dropdown-item {
    margin-bottom: 0;
  }
  .filename {
    margin-bottom: .3rem;
    word-break: break-word;
  }
  .btn-is-option {
    margin-top: 0.25rem;
  }
  .drop-active {
    top: 0;
    bottom: 0;
    right: 0;
    left: 0;
    position: fixed;
    z-index: 9999;
    opacity: .6;
    text-align: center;
    background: #000;
    h3 {
      margin: -.5em 0 0;
      position: absolute;
      top: 50%;
      left: 0;
      right: 0;
      -webkit-transform: translateY(-50%);
      -ms-transform: translateY(-50%);
      transform: translateY(-50%);
      font-size: 40px;
      color: #fff;
      padding: 0;
    }
  }
}

</style>



// WEBPACK FOOTER //
// src/components/upload-files/index.vue